import { Link } from 'react-router';
import type { ContentCollection } from '../content/types';
import { SITE_URL, collectionBasePath, collectionLabel } from '../content/types';
import { articlesInCollection } from '../content';
import { PageMeta } from '../components/PageMeta';
import { MarketingHeader } from '../components/marketing/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { ContentCTA } from '../components/content/ContentCTA';

interface CollectionMeta {
  title: string;
  metaTitle: string;
  description: string;
  eyebrow: string;
  intro: string;
}

const META: Record<ContentCollection, CollectionMeta> = {
  guides: {
    title: 'Customer Feedback Guides',
    metaTitle: 'Customer Feedback Guides | Pollenate',
    description:
      'Practical, no-fluff guides on collecting and acting on customer feedback — feedback widgets, NPS, CSAT, CES, React integration, and HIPAA compliance.',
    eyebrow: 'Guides',
    intro:
      'Practical guides on collecting and acting on customer feedback — from embedding a widget to choosing the right metric.',
  },
  'use-cases': {
    title: 'Use Cases',
    metaTitle: 'Pollenate Use Cases — Website, Product, Docs & AI Feedback',
    description:
      'See how teams use Pollenate to collect website, SaaS product, documentation, AI agent, and HIPAA-compliant feedback — all from one platform.',
    eyebrow: 'Use cases',
    intro:
      'See how teams put Pollenate to work across websites, products, documentation, AI agents, and regulated healthcare.',
  },
  alternatives: {
    title: 'Alternatives & Comparisons',
    metaTitle: 'Best Feedback, Survey & NPS Tools Compared (2026) | Pollenate',
    description:
      'Compare the best feedback, survey, and NPS tools in 2026. Honest, side-by-side alternatives lists to help you find the right fit.',
    eyebrow: 'Alternatives',
    intro:
      'Honest, side-by-side comparisons of the best feedback, survey, and NPS tools in 2026.',
  },
};

interface CollectionIndexPageProps {
  collection: ContentCollection;
}

/** Lists every article in a collection (guides / use-cases / alternatives). */
export function CollectionIndexPage({ collection }: CollectionIndexPageProps) {
  const meta = META[collection];
  const articles = articlesInCollection(collection);
  const url = `${SITE_URL}${collectionBasePath(collection)}`;

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: meta.title,
    description: meta.description,
    url,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: a.title,
        url: `${SITE_URL}${collectionBasePath(collection)}/${a.slug}`,
      })),
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: collectionLabel(collection), item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950">
      <PageMeta title={meta.metaTitle} description={meta.description} url={url} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionLd).replace(/</g, '\\u003c') }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd).replace(/</g, '\\u003c') }}
      />

      <MarketingHeader />

      <main id="main-content" className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
            {meta.eyebrow}
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            {meta.title}
          </h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-neutral-400 max-w-2xl">{meta.intro}</p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={`${collectionBasePath(collection)}/${a.slug}`}
                className="group block rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 hover:border-amber-300 dark:hover:border-amber-700/60 hover:shadow-sm transition-all"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  {a.eyebrow}
                </p>
                <h2 className="mt-2 text-lg font-bold text-slate-900 dark:text-white group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors">
                  {a.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-neutral-400 line-clamp-3">
                  {a.heroSubtitle}
                </p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-amber-600 dark:text-amber-400">
                  Read more &rarr;
                </span>
              </Link>
            ))}
          </div>

          {collection === 'alternatives' && (
            <p className="mt-8 text-sm text-slate-500 dark:text-neutral-400">
              Looking for a head-to-head?{' '}
              <Link to="/vs" className="text-amber-600 dark:text-amber-400 hover:underline">
                See all Pollenate comparisons &rarr;
              </Link>
            </p>
          )}
        </div>
      </main>

      <ContentCTA />
      <MarketingFooter />
    </div>
  );
}
